import React, { useState } from "react";
import { db } from "../config/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const ChampionshipBidForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    clubName: "",
    hostedBefore: null,
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    venueName: "",
    venueAddress: "",
    proposedDates: "",
    equipmentType: "", // AAI or Spieth
    equipmentDetails: "",
    additionalInfo: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (type === "radio") {
      // Handle different radio button groups differently
      if (name === "hostedBefore") {
        setFormData({
          ...formData,
          [name]: value === "yes",
        });
      } else {
        // For other radio groups like equipmentType, just use the value directly
        setFormData({
          ...formData,
          [name]: value,
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Validate required fields
      if (!formData.clubName) {
        throw new Error("Club name is required");
      }
      if (formData.hostedBefore === null) {
        throw new Error("Please indicate if you've hosted before");
      }
      if (!formData.venueName) {
        throw new Error("Venue name is required");
      }
      if (!formData.venueAddress) {
        throw new Error("Venue address is required");
      }
      if (!formData.equipmentType) {
        throw new Error("Please select an equipment type");
      }

      // Add submission to Firestore
      await addDoc(collection(db, "championshipBids"), {
        ...formData,
        submissionDate: serverTimestamp(),
      });

      setSuccess(true);
      setFormData({
        clubName: "",
        hostedBefore: null,
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        facilityName: "",
        facilityAddress: "",
        proposedDates: "",
        equipmentDetails: "",
        additionalInfo: "",
      });

      // Redirect after short delay
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
        >
          {success ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-500 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Submission Successful!
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Thank you for your championship bid submission. We will review your application and contact you soon.
              </p>
              <button
                onClick={() => navigate("/")}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Return to Home
              </button>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 md:p-8">
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  AZ USAG State Championship Bid Form
                </h1>
                <p className="text-blue-100 mt-2">
                  Submit your bid to host the upcoming State Championship
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-4 rounded-lg">
                    {error}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="clubName"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Club Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="clubName"
                    name="clubName"
                    value={formData.clubName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Have you hosted AZ State Championship before? <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="hostedBefore"
                        value="yes"
                        checked={formData.hostedBefore === true}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-700"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">Yes</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="hostedBefore"
                        value="no"
                        checked={formData.hostedBefore === false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-700"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">No</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="contactName"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Contact Name
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contactEmail"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Contact Email
                    </label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contactPhone"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30 mb-6">
                  <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-3">
                    Venue Information <span className="text-red-500">*</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label
                        htmlFor="venueName"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Venue Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="venueName"
                        name="venueName"
                        value={formData.venueName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label
                        htmlFor="venueAddress"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Venue Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="venueAddress"
                        name="venueAddress"
                        value={formData.venueAddress}
                        onChange={handleChange}
                        required
                        placeholder="Full address including city, state and zip"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="proposedDates"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Proposed Dates
                  </label>
                  <input
                    type="text"
                    id="proposedDates"
                    name="proposedDates"
                    value={formData.proposedDates}
                    onChange={handleChange}
                    placeholder="e.g., March 15-17, 2026"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30 mb-6">
                  <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-3">
                    Equipment Information <span className="text-red-500">*</span>
                  </h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      What type of equipment will be used? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="equipmentType"
                          value="AAI"
                          checked={formData.equipmentType === "AAI"}
                          onChange={handleChange}
                          required
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-700"
                        />
                        <span className="ml-2 text-gray-700 dark:text-gray-300">AAI</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="equipmentType"
                          value="Spieth"
                          checked={formData.equipmentType === "Spieth"}
                          onChange={handleChange}
                          required
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-700"
                        />
                        <span className="ml-2 text-gray-700 dark:text-gray-300">Spieth</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="equipmentType"
                          value="Other"
                          checked={formData.equipmentType === "Other"}
                          onChange={handleChange}
                          required
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-700"
                        />
                        <span className="ml-2 text-gray-700 dark:text-gray-300">Other</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label
                      htmlFor="equipmentDetails"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Additional Equipment Details
                    </label>
                    <textarea
                      id="equipmentDetails"
                      name="equipmentDetails"
                      value={formData.equipmentDetails}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Describe additional equipment details, condition, etc."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="additionalInfo"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Additional Information
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Any additional information you'd like to include in your bid"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center ${
                      loading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Submit Bid"
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ChampionshipBidForm;
